/**
 * Modal for editing an existing car. Owner-only — caller is responsible
 * for not rendering it for non-owners. PATCH /api/cars/{id}.
 */

import { useEffect, useState, type FormEvent } from "react";

import Modal from "../../components/Modal";
import { ApiError } from "../../api/client";
import { useUpdateCar } from "../../api/cars";
import type { CarDetail, FuelCategory } from "../../api/types";
import {
  DEFAULT_FUEL_TYPE_BY_CATEGORY,
  FUEL_CATEGORIES,
  FUEL_TYPES_BY_CATEGORY,
  fuelTypeLabel,
} from "./carFormConstants";

interface EditCarModalProps {
  isOpen: boolean;
  onClose: () => void;
  car: CarDetail;
}

export default function EditCarModal({
  isOpen,
  onClose,
  car,
}: EditCarModalProps) {
  const update = useUpdateCar(car.id);

  const [name, setName] = useState(car.name);
  const [manufacturer, setManufacturer] = useState(car.manufacturer ?? "");
  const [model, setModel] = useState(car.model ?? "");
  const [notes, setNotes] = useState(car.notes ?? "");
  const [fuelCategory, setFuelCategory] = useState<FuelCategory>(
    car.fuel_category,
  );
  const [preferredFuel, setPreferredFuel] = useState<string>(
    car.preferred_fuel_type,
  );
  const [error, setError] = useState<string | null>(null);

  // Reseed when reopened.
  useEffect(() => {
    if (isOpen) {
      setName(car.name);
      setManufacturer(car.manufacturer ?? "");
      setModel(car.model ?? "");
      setNotes(car.notes ?? "");
      setFuelCategory(car.fuel_category);
      setPreferredFuel(car.preferred_fuel_type);
      setError(null);
    }
  }, [isOpen, car]);

  // Snap preferred fuel if the user picks a category that excludes it.
  useEffect(() => {
    if (!FUEL_TYPES_BY_CATEGORY[fuelCategory].includes(preferredFuel)) {
      setPreferredFuel(DEFAULT_FUEL_TYPE_BY_CATEGORY[fuelCategory]);
    }
  }, [fuelCategory, preferredFuel]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        name: name.trim(),
        manufacturer: manufacturer.trim(),
        model: model.trim(),
        notes: notes.trim(),
        fuel_category: fuelCategory,
        preferred_fuel_type: preferredFuel,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit car">
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="edit-car-name">
            Name
          </label>
          <input
            id="edit-car-name"
            className="field__input"
            type="text"
            required
            maxLength={255}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field-row">
          <div className="field field--grow">
            <label className="field__label" htmlFor="edit-car-manufacturer">
              Manufacturer
            </label>
            <input
              id="edit-car-manufacturer"
              className="field__input"
              type="text"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
            />
          </div>
          <div className="field field--grow">
            <label className="field__label" htmlFor="edit-car-model">
              Model
            </label>
            <input
              id="edit-car-model"
              className="field__input"
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="edit-car-notes">
            Notes
          </label>
          <textarea
            id="edit-car-notes"
            className="field__input"
            rows={3}
            maxLength={1024}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="field-row">
          <div className="field field--grow">
            <label className="field__label" htmlFor="edit-car-category">
              Fuel category
            </label>
            <select
              id="edit-car-category"
              className="field__input field__select"
              value={fuelCategory}
              onChange={(e) => setFuelCategory(e.target.value as FuelCategory)}
            >
              {FUEL_CATEGORIES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--grow">
            <label className="field__label" htmlFor="edit-car-fuel">
              Preferred fuel
            </label>
            <select
              id="edit-car-fuel"
              className="field__input field__select"
              value={preferredFuel}
              onChange={(e) => setPreferredFuel(e.target.value)}
            >
              {FUEL_TYPES_BY_CATEGORY[fuelCategory].map((ft) => (
                <option key={ft} value={ft}>
                  {fuelTypeLabel(ft)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <p className="form-status" data-variant="error" role="alert">
            {error}
          </p>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="button"
            onClick={onClose}
            disabled={update.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={update.isPending || !name.trim()}
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
